import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { Tooltip, TooltipTrigger, TooltipContent } from '@/components/ui/tooltip'
import {
  Search,
  FileText,
  CheckCircle2,
  XCircle,
  Loader2,
  RotateCcw,
} from 'lucide-react'
import { csrfHeaders } from '@/lib/csrf'
import { toast } from 'sonner'
import type { Project } from '@/types/conversations'

interface LiveSeoSectionProps {
  project: Project
  conversationId?: number
}

export function LiveSeoSection({ project, conversationId }: LiveSeoSectionProps) {
  const { t } = useTranslation('conversations')
  const [starting, setStarting] = useState(false)

  const seoStatus = project.seo_status
  const seoMetadata = project.seo_metadata
  const seoError = project.seo_error
  const hasCustomDomain = !!(project.custom_domain && project.custom_domain_status === 'active')

  const handleStartSeo = async () => {
    if (!conversationId) {
      toast.error(t('live.seo.noConversation'))
      return
    }

    setStarting(true)
    try {
      const response = await fetch(
        `/projects/${project.id}/features/${conversationId}/workflow/start`,
        {
          method: 'POST',
          headers: csrfHeaders(),
          body: JSON.stringify({ name: 'seo_optimize' }),
        }
      )

      if (!response.ok) {
        const data = await response.json()
        toast.error(data.error || data.errors?.[0] || t('live.seo.startFailed'))
      } else {
        toast.success(t('live.seo.started'))
      }
    } catch {
      toast.error(t('live.seo.startFailed'))
    } finally {
      setStarting(false)
    }
  }

  return (
    <div className="p-4 space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-sm font-medium">{t('live.seo.title')}</h3>
          <p className="text-xs text-muted-foreground mt-0.5">
            {t('live.seo.description')}
          </p>
        </div>
        {seoStatus && (
          <SeoStatusBadge status={seoStatus} />
        )}
      </div>

      {/* Error display */}
      {seoStatus === 'failed' && seoError && (
        <div className="rounded-md border border-destructive/50 bg-destructive/5 p-3">
          <div className="flex items-start gap-2">
            <XCircle className="h-4 w-4 text-destructive mt-0.5 shrink-0" />
            <p className="text-xs text-destructive">{seoError}</p>
          </div>
        </div>
      )}

      {/* Action button */}
      {seoStatus !== 'running' && (
        <Tooltip>
          <TooltipTrigger asChild>
            <span className="w-full">
              <Button
                onClick={handleStartSeo}
                disabled={starting || !conversationId || !hasCustomDomain}
                size="sm"
                className="w-full"
              >
                {starting ? (
                  <>
                    <Loader2 className="h-4 w-4 animate-spin mr-2" />
                    {t('live.seo.starting')}
                  </>
                ) : seoMetadata || seoStatus === 'completed' ? (
                  <>
                    <RotateCcw className="h-4 w-4 mr-2" />
                    {t('live.seo.rerunButton')}
                  </>
                ) : seoStatus === 'failed' ? (
                  <>
                    <RotateCcw className="h-4 w-4 mr-2" />
                    {t('live.seo.retryButton')}
                  </>
                ) : (
                  <>
                    <Search className="h-4 w-4 mr-2" />
                    {t('live.seo.runButton')}
                  </>
                )}
              </Button>
            </span>
          </TooltipTrigger>
          {!hasCustomDomain && (
            <TooltipContent>
              {t('live.seo.requiresCustomDomain')}
            </TooltipContent>
          )}
        </Tooltip>
      )}

      {/* Running state */}
      {seoStatus === 'running' && (
        <div className="flex items-center gap-2 text-xs text-muted-foreground">
          <Loader2 className="h-3.5 w-3.5 animate-spin" />
          <span>{t('live.seo.running')}</span>
        </div>
      )}

      {/* Results */}
      {seoMetadata && (
        <div className="space-y-3">
          <div className="grid grid-cols-2 gap-2">
            <StatCard
              label={t('live.seo.results.routesAnalyzed')}
              value={seoMetadata.routes_analyzed}
            />
            <StatCard
              label={t('live.seo.results.pagesRendered')}
              value={seoMetadata.total_pages}
            />
          </div>

          {seoMetadata.routes.length > 0 && (
            <div className="space-y-1.5">
              <h4 className="text-xs font-medium text-muted-foreground">
                {t('live.seo.results.renderedRoutes')}
              </h4>
              <div className="max-h-48 overflow-y-auto space-y-1">
                {seoMetadata.routes.map((route) => (
                  <div
                    key={route.url_path}
                    className="flex items-center gap-2 text-xs py-1 px-2 rounded bg-muted/50"
                  >
                    <FileText className="h-3 w-3 text-muted-foreground shrink-0" />
                    <span className="font-mono truncate flex-1">{route.url_path}</span>
                    {route.title && (
                      <span className="text-muted-foreground truncate max-w-[120px]">
                        {route.title}
                      </span>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}

          {seoMetadata.pre_rendered_at && (
            <p className="text-[10px] text-muted-foreground">
              {t('live.seo.results.lastRun', {
                date: new Date(seoMetadata.pre_rendered_at).toLocaleString(),
              })}
            </p>
          )}
        </div>
      )}
    </div>
  )
}

function SeoStatusBadge({ status }: { status: string }) {
  const { t } = useTranslation('conversations')

  switch (status) {
    case 'running':
      return (
        <Badge variant="outline" className="text-blue-600 border-blue-300">
          <Loader2 className="h-3 w-3 animate-spin mr-1" />
          {t('live.seo.status.running')}
        </Badge>
      )
    case 'completed':
      return (
        <Badge variant="outline" className="text-green-600 border-green-300">
          <CheckCircle2 className="h-3 w-3 mr-1" />
          {t('live.seo.status.completed')}
        </Badge>
      )
    case 'failed':
      return (
        <Badge variant="outline" className="text-destructive border-destructive/30">
          <XCircle className="h-3 w-3 mr-1" />
          {t('live.seo.status.failed')}
        </Badge>
      )
    default:
      return null
  }
}

function StatCard({ label, value }: { label: string; value: number }) {
  return (
    <div className="rounded-md border p-2.5">
      <p className="text-lg font-semibold">{value}</p>
      <p className="text-[10px] text-muted-foreground">{label}</p>
    </div>
  )
}
